import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/CreateUserPage.css';

const CreateUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get user ID from URL params if editing
  const isEditMode = !!id;    // Check if we're in edit mode

  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(isEditMode);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [centers, setCenters] = useState([]);
  const [classes, setClasses] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    center: '',
    password: '',
    confirmPassword: '',
    sendWelcomeEmail: !isEditMode, // Only true for new users
    // Role-specific fields
    specializations: '',  // for teachers
    assignedClasses: [],  // for teachers
    dateOfBirth: '',      // for students (replacing age)
    year: '',            // for students (replacing grade)
    assignedClass: '',   // for students
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch centers and classes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch centers
        const centersResponse = await api.get('/centers');
        const centersData = centersResponse.data.success
          ? centersResponse.data.data
          : centersResponse.data;
        setCenters(Array.isArray(centersData) ? centersData : []);

        // Fetch classes (halaqas)
        const classesResponse = await api.get('/halaqas');
        const classesData = classesResponse.data.success
          ? classesResponse.data.data
          : classesResponse.data;
        setClasses(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Debug log for assigned class
  useEffect(() => {
    if (formData.assignedClass) {
      console.log('Current formData.assignedClass:', formData.assignedClass);
    }
  }, [formData.assignedClass]);

  // Fetch user data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchUser = async () => {
        try {
          setFetchingUser(true);
          const response = await api.get(`/users/${id}`);
          const userData = response.data.success ? response.data.data : response.data;

          console.log('Fetched user data:', userData);
          console.log('Student info:', userData.studentInfo);

          // Log specific fields for debugging assigned class
          if (userData.studentInfo) {
            console.log('Halaqa direct:', userData.studentInfo.halaqa);
            console.log('Halaqa ID:', userData.studentInfo.halaqa?._id);
            console.log('HalaqaId field:', userData.studentInfo.halaqaId);

            // Enhanced debug logging for halaqaId detection
            const possibleHalaqaValues = [
              userData.studentInfo.halaqa?._id,
              userData.studentInfo.halaqaId,
              userData.studentInfo.halaqa
            ];

            console.log('All possible halaqa values:', possibleHalaqaValues);
            console.log('Student info complete dump:', JSON.stringify(userData.studentInfo, null, 2));
          }

          if (userData) {
            // Split name into firstName and lastName if needed
            let firstName = '';
            let lastName = '';

            if (userData.name) {
              const nameParts = userData.name.split(' ');
              firstName = nameParts[0] || '';
              lastName = nameParts.slice(1).join(' ') || '';
            }

            // Set form data with user details
            setFormData({
              firstName,
              lastName,
              email: userData.email || '',
              phone: userData.phone || '',
              role: userData.role || '',
              center: userData.center?._id || userData.center || '',
              // Don't populate password fields for security, but mark them differently for editing mode
              password: '',
              confirmPassword: '',
              sendWelcomeEmail: false,
              // Added flag to track if we're in password update mode
              updatePassword: false,

              // Role-specific fields
              specializations: userData.teacherInfo?.specializations || '',
              assignedClasses: userData.halaqas || userData.teacherInfo?.halaqas || [],
              // Convert age to dateOfBirth if available
              dateOfBirth: userData.studentInfo?.dateOfBirth || '',
              year: userData.studentInfo?.year || userData.studentInfo?.grade || '',
              // Extract assigned class from student info with detailed checks
              assignedClass: (() => {
                // Function to extract the assigned class with multiple fallbacks
                if (!userData.studentInfo) return '';

                // Check for halaqa as object with _id
                if (userData.studentInfo.halaqa && typeof userData.studentInfo.halaqa === 'object' && userData.studentInfo.halaqa._id) {
                  console.log('Found halaqa as object with _id:', userData.studentInfo.halaqa._id);
                  return userData.studentInfo.halaqa._id;
                }

                // Check for halaqa as string
                if (userData.studentInfo.halaqa && typeof userData.studentInfo.halaqa === 'string') {
                  console.log('Found halaqa as string:', userData.studentInfo.halaqa);
                  return userData.studentInfo.halaqa;
                }

                // Check for halaqaId field
                if (userData.studentInfo.halaqaId) {
                  console.log('Found halaqaId field:', userData.studentInfo.halaqaId);
                  return userData.studentInfo.halaqaId;
                }

                // No halaqa found
                console.log('No halaqa found in student info');
                return '';
              })(),
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setFetchingUser(false);
        }
      };

      fetchUser();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelectChange = (e) => {
    const { name, options } = e.target;
    const selectedValues = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      [name]: selectedValues
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Validate required fields
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.role) errors.role = 'Role is required';
    if (!formData.center) errors.center = 'Center is required';

    // Password validation - required for new users or when explicitly updating password
    if (!isEditMode || formData.updatePassword) {
      // Password is required for new users or when explicitly updating
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // In edit mode without updatePassword checked, no validation needed
      // This ensures we don't validate the password field when not updating it
    }
    
    // Role-specific validation
    if (formData.role === 'teacher') {
      // Teacher-specific validation if needed
    } else if (formData.role === 'student') {
      // Both dateOfBirth and year are optional now
      // No validation needed for these fields
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/users/${id}`);
      setShowDeleteModal(false);
      navigate('/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Failed to delete user'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare base user data
      let userData = {};

      // For a new user or a non-nested-field update, use the normal structure
      if (!isEditMode) {
        userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          center: formData.center
        };

        // Add sendWelcomeEmail flag only for new users
        userData.sendWelcomeEmail = formData.sendWelcomeEmail;

        // Add password for new users
        if (formData.password) {
          userData.password = formData.password;
        }

        // Add role-specific data for new users
        if (formData.role === 'teacher') {
          userData.teacherInfo = {
            specializations: formData.specializations,
            halaqas: formData.assignedClasses
          };
        } else if (formData.role === 'student') {
          userData.studentInfo = {
            dateOfBirth: formData.dateOfBirth || '',
            year: formData.year || '',
          };

          // Only add the halaqa if it's actually selected
          if (formData.assignedClass) {
            userData.studentInfo.halaqa = formData.assignedClass;
            userData.studentInfo.halaqaId = formData.assignedClass;
            console.log('Sending assigned class:', formData.assignedClass);
          }

          console.log('Sending student info:', userData.studentInfo);
        }
      } else {
        // For editing an existing user, use a flat structure to work with findByIdAndUpdate
        userData = {
          // Basic user fields
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          center: formData.center
        };

        // Add password only if updatePassword is checked
        if (formData.updatePassword && formData.password) {
          userData.password = formData.password;
        }

        // Handle teacher-specific fields in a flat structure
        if (formData.role === 'teacher') {
          userData.specializations = formData.specializations;
          userData.halaqas = formData.assignedClasses;
          console.log('Sending teacher halaqas:', formData.assignedClasses);
        }
        // Handle student-specific fields in a flat structure
        else if (formData.role === 'student') {
          // Using top-level fields for student info
          userData.dateOfBirth = formData.dateOfBirth || '';
          userData.year = formData.year || '';

          if (formData.assignedClass) {
            userData.halaqaId = formData.assignedClass;
            userData.halaqa = formData.assignedClass;
            console.log('Sending assigned class:', formData.assignedClass);
          }
        }
      }

      let response;

      if (isEditMode) {
        // Update existing user
        response = await api.put(`/users/${id}`, userData);

        // For teacher role, we need to make additional API call to update the teacher record
        if (formData.role === 'teacher' && response.data) {
          try {
            // Check if teacher record exists
            const teacherResponse = await api.get(`/teachers?userId=${id}`);
            const teacherData = teacherResponse.data.success ? teacherResponse.data.data : teacherResponse.data;

            // If teacher record exists, update it
            if (Array.isArray(teacherData) && teacherData.length > 0) {
              const teacherId = teacherData[0]._id;

              // Update teacher record with halaqas and specializations
              await api.put(`/teachers/${teacherId}`, {
                halaqas: formData.assignedClasses,
                specializations: formData.specializations,
                userId: id,
                centerId: formData.center
              });

              // Also update the user record with halaqas directly
              await api.put(`/users/${id}`, {
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                halaqas: formData.assignedClasses
              });

              console.log('Updated teacher record separately and user.halaqas field');
            }
          } catch (teacherError) {
            console.error('Error updating teacher record:', teacherError);
            // Continue even if teacher record update fails
          }
        }

        // For student role, we need to send a direct API call to update studentInfo
        else if (formData.role === 'student' && response.data) {
          try {
            // Make a direct API call to update the user again with student-specific info
            const studentData = {
              studentInfo: {
                dateOfBirth: formData.dateOfBirth || '',
                year: formData.year || ''
              }
            };

            // Add halaqa if provided
            if (formData.assignedClass) {
              studentData.studentInfo.halaqaId = formData.assignedClass;
              console.log('Adding halaqaId to direct student update:', formData.assignedClass);
            }

            // Alternative approach: Update user's name to trigger full save operation
            // This will force mongoose to save the document properly
            const studentUpdateTrigger = {
              name: `${formData.firstName} ${formData.lastName}`.trim(),
              studentInfo: studentData.studentInfo
            };

            // Re-send the update using the regular user update endpoint
            await api.put(`/users/${id}`, studentUpdateTrigger);
            console.log('Sent student update by updating name to trigger save');
          } catch (studentError) {
            console.error('Error with direct student update:', studentError);
            // Continue even if student info update fails
          }
        }
      } else {
        // Create new user
        response = await api.post('/users', userData);
      }

      if (response.data) {
        navigate('/users');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} user`
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-user-container">
      <div className="header">
        <h1>{isEditMode ? 'Edit User' : 'Create New User'}</h1>
        <Link to="/users" className="back-btn">
          Back to Users
        </Link>
      </div>

      <div className="form-card">
        {fetchingUser ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading user data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {formErrors.submit && (
              <div className="error-message">{formErrors.submit}</div>
            )}
          
          <div className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={formErrors.firstName ? 'error' : ''}
                />
                {formErrors.firstName && <div className="error-text">{formErrors.firstName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={formErrors.lastName ? 'error' : ''}
                />
                {formErrors.lastName && <div className="error-text">{formErrors.lastName}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={formErrors.role ? 'error' : ''}
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                </select>
                {formErrors.role && <div className="error-text">{formErrors.role}</div>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="center">Center *</label>
                <select
                  id="center"
                  name="center"
                  value={formData.center}
                  onChange={handleChange}
                  className={formErrors.center ? 'error' : ''}
                >
                  <option value="">Select a center</option>
                  {centers.map(center => (
                    <option key={center._id} value={center._id}>
                      {center.name}
                    </option>
                  ))}
                </select>
                {formErrors.center && <div className="error-text">{formErrors.center}</div>}
              </div>
            </div>
          </div>
          
          {/* Role-specific fields */}
          {formData.role === 'teacher' && (
            <div className="form-section">
              <h2>Teacher Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="specializations">Specializations</label>
                  <input
                    type="text"
                    id="specializations"
                    name="specializations"
                    value={formData.specializations}
                    onChange={handleChange}
                    placeholder="E.g., Tajweed, Hifz, Tafsir"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="assignedClasses">Assigned Classes</label>
                  <select
                    id="assignedClasses"
                    name="assignedClasses"
                    multiple
                    value={formData.assignedClasses}
                    onChange={handleMultiSelectChange}
                    className="multi-select"
                  >
                    {classes.map(classItem => (
                      <option key={classItem._id} value={classItem._id}>
                        {classItem.name}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">Hold Ctrl (or Cmd) to select multiple classes</small>
                </div>
              </div>
            </div>
          )}
          
          {formData.role === 'student' && (
            <div className="form-section">
              <h2>Student Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    placeholder="DD/MM/YYYY"
                  />
                  <small className="helper-text">Optional (DD/MM/YYYY format)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="year">Year</label>
                  <input
                    type="text"
                    id="year"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    placeholder="E.g., Year 10, KS3"
                  />
                  <small className="helper-text">Optional</small>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="assignedClass">Assigned Class</label>
                  <select
                    id="assignedClass"
                    name="assignedClass"
                    value={formData.assignedClass}
                    onChange={(e) => {
                      console.log('Selected class changed to:', e.target.value);
                      handleChange(e);
                    }}
                  >
                    <option value="">Select a class</option>
                    {classes.map(classItem => {
                      const isSelected = classItem._id === formData.assignedClass;
                      if (isSelected) {
                        console.log('Found matching class in dropdown:', classItem.name, classItem._id);
                      }
                      return (
                        <option key={classItem._id} value={classItem._id}>
                          {classItem.name}{isSelected ? ' (Selected)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}
          
          <div className="form-section">
            <h2>Account Settings</h2>

            {isEditMode && (
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="updatePassword"
                    name="updatePassword"
                    checked={formData.updatePassword}
                    onChange={handleChange}
                  />
                  <label htmlFor="updatePassword">
                    Update Password
                  </label>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">{isEditMode ? (formData.updatePassword ? 'New Password *' : 'Password') : 'Password *'}</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'error' : ''}
                  disabled={isEditMode && !formData.updatePassword}
                />
                {formErrors.password && <div className="error-text">{formErrors.password}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">{isEditMode ? (formData.updatePassword ? 'Confirm New Password *' : 'Confirm Password') : 'Confirm Password *'}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={formErrors.confirmPassword ? 'error' : ''}
                  disabled={isEditMode && !formData.updatePassword}
                />
                {formErrors.confirmPassword && (
                  <div className="error-text">{formErrors.confirmPassword}</div>
                )}
              </div>
            </div>

            {!isEditMode && (
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="sendWelcomeEmail"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleChange}
                  />
                  <label htmlFor="sendWelcomeEmail">
                    Send welcome email with login credentials
                  </label>
                </div>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <Link to="/users" className="cancel-btn">
              Cancel
            </Link>
            {isEditMode && (
              <button
                type="button"
                className="delete-btn"
                onClick={() => setShowDeleteModal(true)}
                disabled={loading || fetchingUser}
              >
                Delete User
              </button>
            )}
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || fetchingUser}
            >
              {loading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </form>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Are you sure you want to delete this user?</h3>
            <p>This action cannot be undone. All data associated with this user will be permanently deleted.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={handleDelete}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUserPage;